import {
    contextRepository,
    eventRepository,
    gameRepository,
    matchRepository,
    playerRepository,
    streamRepository,
    teamRepository,
    transactionManager
} from "../../storage/storage";
import { SyncService } from "./sync-service";
import GameService from "../game-service";
import PersistenceError from "../exceptions/persistence-error";
import StreamService from "../stream-service";
import TennisBoxscoreService from "../boxscores/tennis-boxscore-service";

class TennisSyncService extends SyncService {
    /**
     * @param {{[p:string]: *}} remoteMatches
     * @param {{[p:string]: string}} updateMap
     * @returns {Match[]}
     * @private
     */
    _getMergedMatches(remoteMatches, updateMap) {
        return this._updateProps(remoteMatches, updateMap).map((m) =>
            matchRepository.mapToEntity(m)
        );
    }
    /**
     * Replace a local event using remote data, attempt to map and match remote player and team id's to the already present local id's.
     * @param {string} eventId Local eventId
     * @returns {Promise<null | {game: TennisGame, gameContext: TennisGameContext}>}
     */
    async overWriteEventData(eventId) {
        const localEvent = await eventRepository.getById(eventId);
        const localGame = await gameRepository
            .where({ eventId: eventId })
            .first();
        if (localGame) await GameService.deleteGame(localGame.id);
        await StreamService.deleteStreams(localEvent.id);
        const eventData = await this.fetchEventData(localEvent.eventId);
        if (eventData && Object.keys(eventData).length) {
            const {
                game,
                gameContext,
                event,
                players,
                awayTeam,
                homeTeam,
                matches,
                streams
            } = eventData;

            const {
                mergedAwayTeam,
                mergedHomeTeam,
                mergedContext,
                mergedPlayers,
                mergedEvent,
                mergedGame,
                updateMap,
                mergedStreams
            } = await this._mergeEvent(
                event,
                awayTeam,
                homeTeam,
                players,
                gameContext,
                game,
                streams
            );

            const mergedMatches = this._getMergedMatches(matches, updateMap);

            try {
                await transactionManager.transaction(
                    "rw",
                    [
                        gameRepository,
                        eventRepository,
                        contextRepository,
                        teamRepository,
                        playerRepository,
                        matchRepository,
                        streamRepository
                    ],
                    async () => {
                        await Promise.all([
                            gameRepository.put(mergedGame),
                            eventRepository.put(mergedEvent),
                            contextRepository.put(mergedContext),
                            teamRepository.bulkPut([
                                mergedAwayTeam,
                                mergedHomeTeam
                            ]),
                            playerRepository.bulkPut(mergedPlayers),
                            matchRepository.bulkPut(mergedMatches),
                            streamRepository.bulkPut(mergedStreams)
                        ]);
                    }
                );
            } catch (err) {
                console.error(
                    "Error saving remote event: ",
                    eventData,
                    err.stack
                );
                throw new PersistenceError(
                    "Remote event synchronization failed"
                );
            }

            await this._overWriteSync(mergedEvent.id, eventData);

            return {
                game: await gameRepository.getById(game.id),
                gameContext: await contextRepository.getById(gameContext.id)
            };
        }

        return null;
    }

    async collectEventData(gameId) {
        const game = await gameRepository.getById(gameId);
        const event = await eventRepository.getById(game.eventId);
        const { awayTeam, homeTeam } = event.teams;
        const context = await contextRepository.where({ gameId }).first();
        const awayPlayers = await playerRepository
            .where({ teamId: awayTeam.id })
            .toArray();
        const homePlayers = await playerRepository
            .where({ teamId: homeTeam.id })
            .toArray();
        const matches = await matchRepository
            .where({ eventId: game.eventId })
            .toArray();
        const streams = await streamRepository
            .where({ eventId: event.id })
            .toArray();

        const eventData = {
            timeSynced: new Date().toISOString(),
            game: game.toJSON(),
            gameContext: context.toJSON(),
            players: [
                ...awayPlayers.map((player) => player.toJSON()),
                ...homePlayers.map((player) => player.toJSON())
            ],
            event: event.toJSON(),
            awayTeam: awayTeam.toJSON?.() || awayTeam,
            homeTeam: homeTeam.toJSON?.() || homeTeam,
            matches: matches.map((m) => m.toJSON()),
            streams: streams.map((stream) => stream.toJSON())
        };

        return eventData;
    }

    async _performSecondarySync({ id: gameId }) {
        try {
            await TennisBoxscoreService.saveBoxscoreByGameId(gameId);
        } catch (e) {
            console.error("Boxscore could not be submitted");
        }
    }
}

export default new TennisSyncService();
